import { BusinessLanguagesContext } from '@/hooks/useBusinessLanguages';
import { getI18nLangs } from '@/services/muou-i18n';
import type { I18nLangsVO } from '@/types';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PropsWithChildren,
} from 'react';

/**
 * 读不到语言策略时用的内置三语：与 `src/i18n` 的语言包同一组（传输码是 `en`，不是 `en-US`），
 * 主要/基准语言取繁體（全链路默认）。这是降级而不是报错：非超管没有 `sys.i18n.view`，
 * 语言接口 403 是常态，页面照常用内置三语渲染。
 */
const BUILT_IN_LANGUAGES: I18nLangsVO = {
  langs: [
    { priority: 1, code: 'zh-HK', name: '繁體中文', main: true, base: true, tenantDefault: true, ends: [], devEnds: [], coverage: null, note: null },
    { priority: 2, code: 'zh-CN', name: '简体中文', main: false, base: false, tenantDefault: false, ends: [], devEnds: [], coverage: null, note: null },
    { priority: 3, code: 'en', name: 'English', main: false, base: false, tenantDefault: false, ends: [], devEnds: [], coverage: null, note: null },
  ],
  disabledLangs: [],
  mainLang: 'zh-HK',
  baseLang: 'zh-HK',
  defaultLang: 'zh-HK',
  i18nEnabled: true,
  fallbackChain: ['zh-HK'],
};

/** 401/403 是权限常态（非超管没有 `sys.i18n.view`），其余失败至少留一条控制台线索。 */
function isAuthDenied(cause: unknown): boolean {
  const status = (cause as { response?: { status?: number } } | null)?.response
    ?.status;
  return status === 401 || status === 403;
}

/** 语言事实限定在后台布局的生命周期内，离开会话即丢弃。 */
export default function BusinessLanguagesProvider({
  children,
}: PropsWithChildren) {
  const [languages, setLanguages] = useState<I18nLangsVO | null>(null);
  const [loading, setLoading] = useState(true);
  const request = useRef<AbortController | null>(null);

  const reload = useCallback(async () => {
    request.current?.abort();
    const controller = new AbortController();
    request.current = controller;
    setLoading(true);
    try {
      // silent：403 不弹全局提示；失败统一回退内置三语，不把语言策略当成页面故障
      const result = await getI18nLangs(controller.signal, { silent: true });
      if (!controller.signal.aborted) setLanguages(result);
    } catch (cause) {
      if (!controller.signal.aborted) {
        // 403 是权限常态，静默；500/断网留一条线索，别让管理员以为读到的是真实策略
        if (!isAuthDenied(cause)) {
          console.warn('[i18n] 语言策略读取失败，已回退内置三语', cause);
        }
        setLanguages(BUILT_IN_LANGUAGES);
      }
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  }, []);

  useEffect(() => {
    void reload();
    return () => request.current?.abort();
  }, [reload]);

  const value = useMemo(
    () => ({ languages, loading, reload }),
    [languages, loading, reload],
  );
  return (
    <BusinessLanguagesContext.Provider value={value}>
      {children}
    </BusinessLanguagesContext.Provider>
  );
}
